// src/event/eventDefaults.js
// Baked-in fallback config per event. These keep the app rendering when the
// events/{eventId} doc is unreachable (pre-login while rules deny reads, or
// doc not yet seeded). The Firestore doc, once loaded, is merged over these
// section by section — see EventProvider.
//
// Keep club-astra values matching today's live behavior exactly: Stage 2 must
// produce zero visible change on Friday domains.
import {
  CLUB_ASTRA_FRONT_DOOR_COPY,
  CLUB_ASTRA_FRONT_DOOR_RULES,
  NEUTRAL_FRONT_DOOR_COPY,
  NEUTRAL_FRONT_DOOR_RULES,
  cloneFrontDoorValue
} from '../utils/frontDoorRules';
import { CLUB_ASTRA_ENTRY_PERKS, NEUTRAL_ENTRY_PERKS, PANDA_ENTRY_PERKS, cloneEntryPerks } from '../utils/entryPerks';

const PANDA_LOGO_URL =
  'https://storage.googleapis.com/club-astra-guestlist-app.firebasestorage.app/brandAssets/panda-thursdays/versions/vmqunhx34-bhjksl3o/logo.png';
const PANDA_ICON_URL =
  'https://storage.googleapis.com/club-astra-guestlist-app.firebasestorage.app/brandAssets/panda-thursdays/versions/vmqunhx34-bhjksl3o/icon-192.png';
const PANDA_LOGO_SYMBOL_URL =
  'https://storage.googleapis.com/club-astra-guestlist-app.firebasestorage.app/brandAssets/panda-thursdays/versions/vmquo704g-k8hw58ql/logo-symbol.png';
const INTIMO_LOGO_URL =
  'https://storage.googleapis.com/club-astra-guestlist-app.firebasestorage.app/brandAssets/intimo-thursdays/versions/vmv759l14-045us8z0/logo.png';
const INTIMO_ICON_URL =
  'https://storage.googleapis.com/club-astra-guestlist-app.firebasestorage.app/brandAssets/intimo-thursdays/versions/vmv6qprws-xmb02oi8/icon-192.png';
const CLUB_ASTRA_RETAINER_JOTFORM_URL = 'https://form.jotform.com/jsform/252301092820041';

export const EVENT_DEFAULTS = {
  'club-astra': {
    active: true,
    brand: {
      name: 'Club Astra',
      shortName: 'Club Astra',
      logo: '/ClubAstraLogoSide.png',
      themeColor: '#111111',
      accent: '#e8c97a',
      doorHeaderBg: '#0E1420',
      doorHeaderSubbarBg: '#161D2C',
      doorHeaderEmailColor: '#8A8576',
      doorHeaderQrColor: '#C9A86A',
      doorHeaderSignOutColor: '#D4B87A',
      doorHeaderBorderColor: '#3A3D42'
    },
    night: {
      weekday: 5, // Friday
      timezone: 'Australia/Melbourne',
      dayRolloverHour: 6 // before 6am counts as last night's event
    },
    features: {
      hostPhotoSignIn: false,
      booths: true,
      dinnerBookings: true,
      publicRsvp: true,
      qrCheckin: true
    },
    rules: {
      frontDoor: cloneFrontDoorValue(CLUB_ASTRA_FRONT_DOOR_RULES)
    },
    entryPerks: cloneEntryPerks(CLUB_ASTRA_ENTRY_PERKS),
    retainerForm: {
      jotformUrl: CLUB_ASTRA_RETAINER_JOTFORM_URL
    },
    managementFreeList: {
      enabled: true,
      defaultKey: 'emerson',
      aliases: {},
      lists: {
        emerson: {
          enabled: true,
          role: 'emerson',
          routeSlug: 'emerson',
          title: 'Emerson - Management Free List',
          reportTitle: 'Emerson - Management Frees Report',
          listName: '#Emerson Management Frees List',
          listType: 'Guest List',
          organiserLabel: "Management's Name",
          teamName: '',
          includeTeamReports: false
        },
        'yours-truly': {
          enabled: true,
          role: 'yours_truly',
          routeSlug: 'yours-truly',
          title: "Yours Truly - Management's Free List",
          reportTitle: 'Yours Truly - Reports',
          listName: '#Yours Truly Free List',
          listType: 'Guest List',
          organiserLabel: "Management's Name",
          teamName: 'Yours Truly',
          includeTeamReports: true
        }
      }
    },
    copy: {
      frontDoor: cloneFrontDoorValue(CLUB_ASTRA_FRONT_DOOR_COPY)
    }
  },

  'panda-thursdays': {
    active: true,
    brand: {
      name: 'Panda Thursdays',
      shortName: 'Panda',
      logo: PANDA_LOGO_URL,
      logoSymbol: PANDA_LOGO_SYMBOL_URL,
      icon: PANDA_ICON_URL,
      themeColor: '#971f3a',
      accent: '#b02645',
      doorHeaderBg: '#120811',
      doorHeaderSubbarBg: '#1C0D15',
      doorHeaderEmailColor: '#A8949C',
      doorHeaderQrColor: '#B02645',
      doorHeaderSignOutColor: '#B02645',
      doorHeaderBorderColor: '#4A2030'
    },
    night: {
      weekday: 4, // Thursday
      timezone: 'Australia/Melbourne',
      dayRolloverHour: 6
    },
    features: {
      hostPhotoSignIn: true,
      booths: true,
      dinnerBookings: false,
      publicRsvp: true,
      qrCheckin: true
    },
    rules: {
      frontDoor: cloneFrontDoorValue(NEUTRAL_FRONT_DOOR_RULES)
    },
    entryPerks: cloneEntryPerks(PANDA_ENTRY_PERKS),
    retainerForm: {
      jotformUrl: ''
    },
    managementFreeList: {
      enabled: true,
      defaultKey: 'bar-bambi',
      aliases: {
        emerson: 'bar-bambi',
        bambi: 'bar-bambi'
      },
      lists: {
        'bar-bambi': {
          enabled: true,
          role: 'bar_bambi',
          routeSlug: 'bar-bambi',
          title: 'Bar Bambi - Management Free List',
          reportTitle: 'Bar Bambi - Management Frees Report',
          listName: '#Bar Bambi Management Frees List',
          listType: 'Guest List',
          organiserLabel: "Management's Name",
          teamName: 'Bar Bambi',
          includeTeamReports: false
        }
      }
    },
    copy: {
      frontDoor: cloneFrontDoorValue(NEUTRAL_FRONT_DOOR_COPY)
    }
  },

  'intimo-thursdays': {
    active: true,
    brand: {
      name: 'Intimo Thursdays',
      shortName: 'Intimo Thursdays',
      logo: INTIMO_LOGO_URL,
      icon: INTIMO_ICON_URL,
      themeColor: '#0d3b2a',
      accent: '#f1e6d1',
      doorHeaderBg: '#0d3b2a',
      doorHeaderSubbarBg: '#0d3b2a',
      doorHeaderEmailColor: '#c99d86',
      doorHeaderQrColor: '#c99d86',
      doorHeaderSignOutColor: '#7a1017',
      doorHeaderBorderColor: '#11100e'
    },
    night: {
      weekday: 4, // Thursday
      timezone: 'Australia/Melbourne',
      dayRolloverHour: 6
    },
    features: {
      hostPhotoSignIn: true,
      booths: false,
      dinnerBookings: true,
      publicRsvp: true,
      qrCheckin: true
    },
    rules: {
      frontDoor: cloneFrontDoorValue(NEUTRAL_FRONT_DOOR_RULES)
    },
    entryPerks: cloneEntryPerks(NEUTRAL_ENTRY_PERKS),
    retainerForm: {
      jotformUrl: ''
    },
    copy: {
      frontDoor: cloneFrontDoorValue(NEUTRAL_FRONT_DOOR_COPY)
    }
  }
};

export const NEUTRAL_EVENT_DEFAULT = {
  active: false,
  brand: {
    name: 'Guestlist',
    shortName: 'Guestlist',
    logo: '',
    themeColor: '#111111',
    accent: '#ffffff',
    doorHeaderBg: '#0E1420',
    doorHeaderSubbarBg: '#161D2C',
    doorHeaderEmailColor: '#8A8576',
    doorHeaderQrColor: '#C9A86A',
    doorHeaderSignOutColor: '#D4B87A',
    doorHeaderBorderColor: '#3A3D42'
  },
  night: {
    weekday: 5,
    timezone: 'Australia/Melbourne',
    dayRolloverHour: 6
  },
  features: {
    hostPhotoSignIn: false,
    booths: false,
    dinnerBookings: false,
    publicRsvp: false,
    qrCheckin: false
  },
  rules: {
    frontDoor: cloneFrontDoorValue(NEUTRAL_FRONT_DOOR_RULES)
  },
  entryPerks: cloneEntryPerks(NEUTRAL_ENTRY_PERKS),
  retainerForm: {
    jotformUrl: ''
  },
  managementFreeList: {
    enabled: false,
    defaultKey: '',
    aliases: {},
    lists: {}
  },
  copy: {
    frontDoor: cloneFrontDoorValue(NEUTRAL_FRONT_DOOR_COPY)
  }
};
